import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Send, Lock, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CaseChatThread — Polling-based chat thread anchored to a tax_case.
 * Both customer & assigned consultant use this same component.
 *
 * Privacy: All messages are PII-masked server-side (phone / email / UPI / @handle).
 * Customer NEVER sees the consultant's real name — only their alias.
 */
const CaseChatThread = ({ caseId, currentRole = 'customer', testIdPrefix = 'chat' }) => {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('arthik_token')}` });

  const fetchMessages = async (silent = false) => {
    try {
      const r = await axios.get(`${API}/tax-cases/${caseId}/chat`, { headers: headers() });
      setMessages(r.data || []);
    } catch (e) {
      if (!silent) toast.error('Could not load chat');
    } finally {
      setLoadingInitial(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchMessages();
    pollRef.current = setInterval(() => fetchMessages(true), 6000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/tax-cases/${caseId}/chat`, { body: text }, { headers: headers() });
      setMessages((m) => [...m, r.data]);
      setBody('');
      if (r.data.was_redacted) {
        toast.warning('Phone/email/UPI auto-redacted for safety. Chat only via Arthik.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const isMine = (m) => {
    if (currentRole === 'tax_consultant' || currentRole === 'admin') return m.sender_role === 'tax_consultant' || m.sender_role === 'admin';
    return m.sender_role === 'customer';
  };

  return (
    <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px dashed #E2E8F0' }} data-testid={`${testIdPrefix}-${caseId}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: open ? '#FFF7ED' : 'transparent', border: '1px solid ' + (open ? '#FED7AA' : '#E2E8F0'),
          color: '#9A3412', padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
        }}
        data-testid={`${testIdPrefix}-toggle-${caseId}`}
      >
        <MessageSquare size={13} /> {open ? 'Hide chat' : 'Open chat'}
        {!open && messages.length > 0 && <span style={{ background: '#FF6B35', color: 'white', padding: '0 7px', borderRadius: 999, fontSize: '0.7rem' }}>{messages.length}</span>}
      </button>

      {open && (
        <div style={{ marginTop: '0.85rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 0.8rem', background: '#FFFBEB', borderBottom: '1px solid #FED7AA', fontSize: '0.72rem', color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lock size={12} /> Private chat — phone / email / UPI / @handles are auto-redacted. Sharing personal contact is prohibited.
          </div>

          <div style={{ maxHeight: 340, overflowY: 'auto', padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }} data-testid={`${testIdPrefix}-messages-${caseId}`}>
            {loadingInitial ? (
              <div style={{ textAlign: 'center', color: '#94A3B8', padding: '1rem' }}><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94A3B8', padding: '1rem', fontSize: '0.85rem' }}>
                No messages yet. Say hi to {currentRole === 'tax_consultant' || currentRole === 'admin' ? 'your client' : 'your CA'} 👋
              </div>
            ) : (
              messages.map((m) => {
                const mine = isMine(m);
                return (
                  <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '78%' }} data-testid={`${testIdPrefix}-msg-${m.id}`}>
                    <div style={{ fontSize: '0.68rem', color: '#94A3B8', marginBottom: 2, textAlign: mine ? 'right' : 'left' }}>
                      {m.sender_display} · {new Date(m.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{
                      padding: '7px 12px', borderRadius: 14,
                      background: mine ? 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)' : 'white',
                      color: mine ? 'white' : '#0F172A',
                      border: '1px solid ' + (mine ? '#F97316' : '#E2E8F0'),
                      fontSize: '0.88rem', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {m.body}
                    </div>
                    {m.was_redacted && (
                      <div style={{ fontSize: '0.66rem', color: '#9A3412', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>
                        ⚠ Some content was redacted
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div style={{ display: 'flex', gap: 6, padding: '0.6rem', background: 'white', borderTop: '1px solid #E2E8F0' }}>
            <input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your message…"
              maxLength={2000}
              disabled={sending}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 999, fontSize: '0.9rem', outline: 'none' }}
              data-testid={`${testIdPrefix}-input-${caseId}`}
            />
            <button
              onClick={send}
              disabled={sending || !body.trim()}
              style={{
                padding: '0 16px', borderRadius: 999, border: 'none', cursor: sending || !body.trim() ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)', color: 'white', fontWeight: 700, fontSize: '0.85rem',
                display: 'inline-flex', alignItems: 'center', gap: 6, opacity: sending || !body.trim() ? 0.6 : 1,
              }}
              data-testid={`${testIdPrefix}-send-${caseId}`}
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseChatThread;
